import { useState } from "react";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { AxiosResponse } from "axios";

import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { cn } from "@/shared/lib/utils";
import { Spinner } from "@/shared/ui/spinner";
import {
  useAssignCuratorToTeamlead,
  useCreateCurator,
} from "@/entities/curator/api";
import { useSessionStore } from "@/entities/session";
import { Curator } from "@/entities/curator";

const accountSchema = z.object({
  tgUsername: z.string().min(1, ""),
  tgNumber: z.string().min(1, ""),
  tgId: z.string().min(1, ""),
  contactUrl: z.string().optional(),
});

type AccountFormValues = z.infer<typeof accountSchema>;

export const CreateCuratorForm = () => {
  const { mutateAsync, isPending } = useCreateCurator();
  const { mutateAsync: assignCurator } = useAssignCuratorToTeamlead();
  const { user } = useSessionStore();
  const [isHyperLinkOpen, setIsHyperLinkOpen] = useState(false);

  const { role, _id } = user!;

  const form = useForm<z.infer<typeof accountSchema>>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      tgUsername: "",
      tgNumber: "",
      tgId: "",
      contactUrl: undefined,
    },
    mode: "all",
  });

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (!value.startsWith("@")) {
      value = "@" + value;
    }
    value = value.replace(/[^a-zA-Z0-9@_]/g, "");

    if (value.startsWith("@@")) {
      value = value.slice(1);
    }

    form.setValue("tgUsername", value, { shouldValidate: true });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (value.startsWith("8")) {
      value = `+7${value.slice(1)}`;
    }

    value = value.replace(/[^0-9\s]/g, "");

    form.setValue("tgNumber", value, { shouldValidate: true });
  };

  const handleTgIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    value = value.replace(/[^0-9]/g, "");

    form.setValue("tgId", value, { shouldValidate: true });
  };

  const onSubmit = (data: AccountFormValues) => {
    mutateAsync(data).then((response: AxiosResponse<{ data: Curator }>) => {
      if (!_id || role !== "teamlead") return;

      assignCurator({
        teamleadId: _id,
        curatorId: response.data.data._id,
      });
    });
    form.reset();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="tgUsername"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel htmlFor="tgUsername">Telegram</FormLabel>
              <FormControl>
                <Input
                  id="tgUsername"
                  type="text"
                  placeholder="@username"
                  {...field}
                  onChange={handleUsernameChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tgNumber"
          render={({ field }) => (
            <FormItem className="flex-2">
              <FormLabel htmlFor="tgNumber">Телефон</FormLabel>
              <FormControl>
                <Input
                  id="tgNumber"
                  type="text"
                  placeholder="+..."
                  {...field}
                  onChange={handlePhoneChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tgId"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel htmlFor="tgId" className="text-nowrap">
                Telegram ID
              </FormLabel>
              <FormControl>
                <Input
                  id="tgId"
                  type="text"
                  placeholder="..."
                  {...field}
                  onChange={handleTgIdChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isHyperLinkOpen && (
          <FormField
            control={form.control}
            name="contactUrl"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel htmlFor="contactUrl">Ссылка</FormLabel>
                <FormControl>
                  <Input
                    id="link"
                    type="text"
                    placeholder="https://..."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full place-self-end"
          onClick={() => setIsHyperLinkOpen((prev) => !prev)}
        >
          🔗
        </Button>

        <div />

        <Button
          type="submit"
          className={cn(
            "col-span-2 mt-[5px] h-10 w-full flex-1",
            JSON.stringify(form.formState.errors) ? "self-end" : "self-center",
          )}
          disabled={!form.formState.isValid}
        >
          {isPending ? <Spinner /> : "Добавить"}
        </Button>
      </form>
    </Form>
  );
};
